// CX Services — English copy. Imported into en.ts as `cx`.
// Tone: professional, factual, no exclamation marks.

export const cxEn = {
  meta: {
    hubTitle: 'Customer Experience Operations from the EU — ADD Individual Solutions',
    hubDescription: 'German-language and multilingual customer support, back-office and AI-enabled CX, delivered from Cyprus and Transylvania under one EU contract. Pilot-first, SLA-backed, GDPR-native.',
    assessmentTitle: 'Request a CX Operations Assessment — ADD Individual Solutions',
    assessmentDescription: 'A two-week review of your customer operation: volumes, channels, KPIs and cost-to-serve. You receive a baseline, a target model and a pilot plan. No obligation.',
  },

  breadcrumbs: { home: 'Home', cx: 'CX Services', services: 'Service lines', assessment: 'Assessment' },

  hero: {
    eyebrow: 'Customer Experience Operations · EU',
    title: 'Customer support that answers to your regulator, your CFO and your customers.',
    subtitle: 'German-language and multilingual customer support, back-office and AI-enabled CX — delivered from Cyprus and Transylvania under one EU contract. Founded and run by an operator with fifteen years at SYKES Enterprises.',
    cta1: 'Request a CX Operations Assessment',
    cta2: 'See the delivery model',
  },

  needs: {
    tag: 'What buyers of outsourced CX need',
    title: 'Three requirements. One delivery model.',
    items: [
      { icon: 'shield', title: 'Compliance without compromise', text: 'Customer data processed inside the EU, under GDPR, with no third-country transfer questions to answer to your DPO or regulator.' },
      { icon: 'trending', title: 'Cost-to-serve discipline', text: 'German-language delivery at 25–40% below DACH onshore bill rates, with a transparent per-FTE or per-hour cost build-up.' },
      { icon: 'usercheck', title: 'Continuity of quality', text: 'A named operator accountable for the account, SLA-backed delivery, and a pilot-first entry that limits your exposure.' },
    ],
  },

  services: {
    tag: 'Service lines',
    title: 'Four service lines, one quality framework',
    subtitle: 'German-language support is the core. Multilingual support, back-office and AI-enabled CX are bundled around it, run by the same team under the same KPIs.',
    learnMore: 'Service details',
    items: [
      { slug: 'german-customer-support', icon: 'headphones', title: 'German-language customer support', summary: 'Voice, e-mail, chat and messaging; 1st and 2nd level; complaints and retention. Delivered by native and near-native German speakers.', tags: ['1st & 2nd level', 'Complaints', 'Retention'] },
      { slug: 'multilingual-support', icon: 'globe', title: 'Multilingual support', summary: 'English as standard; Romanian, Greek and further EU languages on request. One team, one quality framework across markets.', tags: ['EN standard', 'EU languages', 'One QA framework'] },
      { slug: 'back-office-data-processing', icon: 'database', title: 'Back-office and data processing', summary: 'Order processing, KYC and onboarding, document handling, data entry and verification — with four-eyes controls where required.', tags: ['KYC', 'Order processing', 'Four-eyes'] },
      { slug: 'cx-consulting-ai', icon: 'cpu', title: 'CX consulting and AI-enabled CX', summary: 'Operational assessment, QA and WFM design, CSAT/NPS programmes; agent-assist, intelligent deflection and automated quality scoring.', tags: ['Assessment', 'Agent assist', 'QA automation'] },
    ],
  },

  delivery: {
    tag: 'Delivery model',
    title: 'One EU contract, two locations',
    cyprus: {
      title: 'Cyprus — hub',
      points: [
        'EU member since 2004, euro since 2008; corporate tax 15% (2026)',
        'Established regulated-services hub: CySEC/MiFID investment firms, iGaming, shipping, tourism',
        'English-speaking business environment; EET time zone (DACH +1h)',
        'Role: contract, compliance, management, quality assurance, premium German-language lines',
      ],
    },
    transylvania: {
      title: 'Transylvania — delivery',
      points: [
        'Romania: EU member, Schengen, GDPR — no data-transfer barrier',
        'Workforce strong in English, French, German, Italian and Spanish',
        'Sibiu: oldest German-language school in Romania (1380); 100+ German-capital companies; German investors account for roughly half of local private-sector jobs',
        'Role: scalable capacity and language depth at a lower cost base',
      ],
    },
    foot: 'The client contracts with the Cyprus entity. Data stays in the EU. Management and quality assurance sit with the operator.',
  },

  cyprus: {
    tag: 'Why Cyprus',
    title: 'The right base for regulated customer operations',
    stats: [
      { value: '2004', label: 'EU member state' },
      { value: '2008', label: 'Euro-zone member' },
      { value: '15%', label: 'Corporate income tax (2026)' },
      { value: '€22,000', label: 'Tax-free personal income threshold (2026) — a recruiting advantage' },
    ],
    body: 'Cyprus is not an experiment in customer operations. Regulated, multilingual support teams already run here for CySEC-licensed brokers, payment institutions and iGaming operators serving the whole EU. That is the operating environment ADD is built in — and the one buyers in fintech, trading, travel and e-commerce expect.',
  },

  sectors: {
    tag: 'Sectors',
    title: 'Where EU data residency and language quality decide the vendor',
    items: [
      { icon: 'creditcard', title: 'Fintech and payments', text: 'KYC and onboarding, disputes, chargebacks, account servicing — where EU data residency is a hard requirement.' },
      { icon: 'barchart', title: 'Trading and brokerage', text: 'CySEC/BaFin-regulated client support, funding and withdrawal queries, complaints handling with audit trail.' },
      { icon: 'target', title: 'iGaming', text: '24/7 multilingual player support, responsible-gaming workflows, payments and verification.' },
      { icon: 'navigation', title: 'Travel and mobility', text: 'Bookings, disruption handling, roadside and rental assistance across time zones.' },
      { icon: 'cart', title: 'E-commerce and subscription', text: 'Order and returns handling, complaints and retention, peak-season scaling.' },
    ],
  },

  operate: {
    tag: 'How we operate',
    title: 'Assess, design, pilot, scale',
    steps: [
      { n: '1', title: 'Assess', text: 'Two weeks: volumes, channels, KPIs, cost-to-serve, QA baseline.' },
      { n: '2', title: 'Design', text: 'Target model, staffing plan, SLA set, transition plan.' },
      { n: '3', title: 'Pilot', text: '3–5 FTE, 90 days, weekly reviews, exit clause.' },
      { n: '4', title: 'Scale', text: 'Ramp in agreed steps; monthly business reviews.' },
    ],
    standardsTitle: 'Operating standards',
    standards: [
      'KPI stack: CSAT, FCR, AHT, SLA adherence, QA score — reported weekly',
      'Workforce management: forecasting, scheduling, intraday control',
      'QA calibration sessions with the client; 100% coverage where automated scoring is deployed',
      'Transition playbook: 30/60/90-day plan with named owners',
      'Security: EU-only processing, GDPR data-processing agreement, ISO-aligned controls',
    ],
  },

  commercial: {
    tag: 'Commercial model',
    title: 'Pilot-first, open-book',
    pilot: {
      title: 'Pilot-first entry',
      points: [
        '3–5 FTE for 90 days, SLA-backed, with an exit clause',
        'Open-book cost build-up per FTE — no hidden set-up or technology fees',
        'Weekly performance reviews during the pilot',
        'Scale only against agreed KPIs',
      ],
    },
    pricing: {
      title: 'Pricing',
      points: [
        'Per FTE per month or per productive hour — your choice',
        'German-language support: indicative €27–35 per productive hour',
        'Reference: DACH onshore BPO bill rates €40–55/h; in-house agent €55–90k per year fully loaded',
        'Volume tiers from 10 and 20 FTE; final pricing after the assessment',
      ],
    },
  },

  ai: {
    tag: 'AI-enabled CX',
    title: 'Deployed by operators, measured on KPIs',
    items: [
      { icon: 'message', title: 'Agent assist', text: 'LLM-drafted replies and knowledge retrieval inside the agent desktop. Measured on AHT and FCR, not on demos.' },
      { icon: 'layers', title: 'Intelligent deflection', text: 'Self-service and conversational automation for repetitive contacts, with clean hand-off to a human agent.' },
      { icon: 'check', title: 'Quality at 100% coverage', text: 'Automated scoring of every interaction against your QA framework; calibration stays with people.' },
      { icon: 'activity', title: 'Operational analytics', text: 'Contact-driver analysis and forecasting that feed workforce planning and root-cause fixes.' },
    ],
    foot: 'ADD builds these tools in-house. They are introduced after the operation is stable, never as a substitute for it.',
  },

  leadership: {
    tag: 'Leadership',
    title: 'Run by an operator, not a sales team',
    name: 'Daniel Dobos',
    role: 'Founder & Managing Director · Diplom-Kaufmann',
    lead: 'Fifteen years at SYKES Enterprises, most recently Senior Operations Manager for Romania and Cyprus (Cluj-Napoca, Larnaca), leading German-language accounts of more than 280 FTE.',
    facts: [
      'Deutsche Telekom — German-language front- and back-office',
      '1&1 — German-language front- and back-office',
      'Klarna — 1st and 2nd level, German and English',
      'PlayStation — German, Nordic languages and Turkish',
      'Diplom-Kaufmann: German university degree in business economics',
    ],
    meansTitle: 'What this means for your programme',
    means: [
      'Ramp-ups, transitions and site leadership executed personally — for Deutsche Telekom, 1&1, Klarna and PlayStation',
      'Workforce management, QA calibration and KPI governance applied from day one',
      'Direct escalation path to the person accountable for your results',
    ],
  },

  governance: {
    tag: 'Risk and governance',
    title: 'Named risks, documented controls',
    items: [
      { icon: 'lock', title: 'Data protection', text: 'GDPR data-processing agreement, EU-only processing, documented sub-processors, access controls and audit logs.' },
      { icon: 'refresh', title: 'Business continuity', text: 'Two EU locations, remote-capable teams, documented failover for the German-language lines.' },
      { icon: 'users', title: 'Staffing', text: 'Recruitment in two labour markets; German-speaker pipeline in Transylvania; attrition buffer built into the plan.' },
      { icon: 'eye', title: 'Transparency', text: 'Open-book pilot, weekly reviews, KPI reporting you can verify.' },
      { icon: 'logout', title: 'Exit', text: 'Documented handback of knowledge base, processes and data — agreed before the pilot starts.' },
    ],
  },

  cta: {
    title: 'Next step: a two-week CX Operations Assessment',
    subtitle: 'We review volumes, channels, KPIs and cost-to-serve. You receive a baseline, a target model and a pilot plan with staffing, SLA and price. No obligation to proceed.',
    button: 'Request the assessment',
    secondary: 'Or write to contact@add-individual-solutions.com',
  },

  teaser: {
    tag: 'Customer Experience Operations',
    title: 'Customer support from the EU, run by an operator',
    text: 'German-language and multilingual customer support, back-office and AI-enabled CX — delivered from Cyprus and Transylvania under one EU contract.',
    points: ['EU-only processing under GDPR', '25–40% below DACH onshore bill rates', 'Pilot-first: 3–5 FTE, 90 days, exit clause'],
    cta: 'Explore CX services',
  },

  assessment: {
    title: 'Request a CX Operations Assessment',
    subtitle: 'Two weeks. Your volumes, channels, KPIs and cost-to-serve, reviewed by an operator. You keep the baseline whether or not you proceed.',
    review: {
      title: 'What we review',
      items: ['Contact volumes, channels and drivers', 'AHT, FCR, CSAT and SLA performance', 'Cost-to-serve per contact and per FTE', 'QA framework and calibration'],
    },
    receive: {
      title: 'What you receive',
      items: ['A baseline of your current operation', 'A target model for the outsourced scope', 'A pilot plan with staffing, SLA and price', 'No obligation to proceed'],
    },
    form: {
      title: 'Tell us about your operation',
      companySection: 'Company',
      contactSection: 'Contact',
      operationSection: 'Operation',
      companyName: 'Company name',
      website: 'Website',
      industry: 'Industry',
      industryOptions: [
        { value: '', label: 'Select an industry' },
        { value: 'fintech', label: 'Fintech and payments' },
        { value: 'trading', label: 'Trading and brokerage' },
        { value: 'igaming', label: 'iGaming' },
        { value: 'travel', label: 'Travel and mobility' },
        { value: 'ecommerce', label: 'E-commerce and subscription' },
        { value: 'medtech', label: 'Medtech / health-adjacent' },
        { value: 'saas', label: 'SaaS / technology' },
        { value: 'other', label: 'Other' },
      ],
      country: 'Country',
      contactName: 'Your name',
      jobTitle: 'Job title',
      email: 'Business e-mail',
      phone: 'Phone (optional)',
      fteBand: 'Customer-service FTE today',
      fteOptions: [
        { value: '', label: 'Select a range' },
        { value: '1-10', label: '1–10' },
        { value: '11-25', label: '11–25' },
        { value: '26-50', label: '26–50' },
        { value: '51-100', label: '51–100' },
        { value: '101-250', label: '101–250' },
        { value: '250+', label: 'More than 250' },
      ],
      languages: 'Languages required',
      languageOptions: [
        { value: 'de', label: 'German' },
        { value: 'en', label: 'English' },
        { value: 'ro', label: 'Romanian' },
        { value: 'other', label: 'Other' },
      ],
      channels: 'Channels in scope',
      channelOptions: [
        { value: 'voice', label: 'Voice' },
        { value: 'email', label: 'E-mail' },
        { value: 'chat', label: 'Chat / messaging' },
        { value: 'backoffice', label: 'Back-office' },
      ],
      currentModel: 'Current delivery model',
      modelOptions: [
        { value: '', label: 'Select' },
        { value: 'in_house', label: 'In-house' },
        { value: 'outsourced_onshore', label: 'Outsourced — onshore' },
        { value: 'outsourced_nearshore', label: 'Outsourced — nearshore / offshore' },
        { value: 'mixed', label: 'Mixed' },
      ],
      timeline: 'Timeline',
      timelineOptions: [
        { value: '', label: 'Select' },
        { value: 'immediate', label: 'Immediate' },
        { value: '1-3_months', label: '1–3 months' },
        { value: '3-6_months', label: '3–6 months' },
        { value: 'exploring', label: 'Exploring' },
      ],
      message: 'Context (optional)',
      messagePlaceholder: 'Current pain points, systems in use, anything we should know before the scoping call.',
      consent: 'I agree that ADD Individual Solutions Ltd processes the data above to respond to this request, as described in the',
      consentLink: 'Privacy Policy',
      submit: 'Request the assessment',
      sending: 'Sending…',
      verify: 'Please complete the verification.',
      success: 'Your request has been received. You will hear from Daniel Dobos within one business day.',
      error: 'The request could not be sent. Please try again or write to contact@add-individual-solutions.com.',
      required: 'Required',
    },
  },

  servicePages: {
    'german-customer-support': {
      title: 'German-language customer support',
      metaDescription: 'German-language customer support from the EU — voice, e-mail, chat and messaging, 1st and 2nd level, complaints and retention. Delivered from Cyprus and Transylvania by ADD Individual Solutions.',
      heroDescription: 'Native and near-native German-speaking agents, run under one quality framework from two EU locations. First and second level, complaints and retention, on voice, e-mail, chat and messaging — with the KPI stack, QA calibration and workforce management a DACH customer operation expects.',
      sections: [
        { heading: 'Scope', content: 'Inbound customer service and technical first level; second-level case handling; complaints and escalation management; retention and win-back; order, account and billing support. Coverage from business hours to 24/7 in agreed steps, in the EET time zone (DACH +1h).' },
        { heading: 'How we deliver', content: 'Teams are recruited in two labour markets — Cyprus and Transylvania — and led from the Cyprus hub. Every programme starts with a two-week assessment and a documented transition plan (30/60/90 days). Workforce management covers forecasting, scheduling and intraday control; team leads run at a ratio of one to nine.' },
        { heading: 'Quality and compliance', content: 'CSAT, FCR, AHT, SLA adherence and QA score are reported weekly. QA calibration sessions run with your team; where automated scoring is deployed, coverage is 100% of interactions. Processing is EU-only under a GDPR data-processing agreement, with documented sub-processors and access controls.' },
        { heading: 'Commercial terms', content: 'Pilot of 3–5 FTE for 90 days, SLA-backed, with an exit clause. Pricing per FTE per month or per productive hour — indicative €27–35 per productive hour, against DACH onshore bill rates of €40–55. Volume tiers from 10 and 20 FTE. Final pricing follows the assessment.' },
      ],
    },
    'multilingual-support': {
      title: 'Multilingual support',
      metaDescription: 'Multilingual customer support from the EU — English as standard, Romanian, Greek and further EU languages on request, under one quality framework. ADD Individual Solutions, Cyprus and Transylvania.',
      heroDescription: 'One team, one quality framework across markets. English is standard; Romanian and Greek are available from our two locations; further EU languages are added on request through the same recruitment and QA process.',
      sections: [
        { heading: 'Scope', content: 'Customer service, technical first level and back-office in English and additional languages, for companies serving several European markets from one operation. Language coverage and hours are agreed per line and can be extended in steps.' },
        { heading: 'How we deliver', content: 'Multilingual lines share supervision, workforce management and QA with the German-language core, which keeps the cost per language line low. Cyprus provides English- and Greek-speaking capacity; Transylvania provides Romanian, German and further languages from a workforce that is strong in English, French, German, Italian and Spanish.' },
        { heading: 'Quality and compliance', content: 'The same KPI stack applies to every language: CSAT, FCR, AHT, SLA adherence and QA score, reported weekly. Language quality is assessed at recruitment and re-checked in QA calibration. EU-only processing under GDPR, with a documented sub-processor list.' },
        { heading: 'Commercial terms', content: 'Per FTE per month or per productive hour, with volume tiers from 10 and 20 FTE. Language lines are priced individually; English and Romanian are the most cost-efficient. Pilot-first entry with an exit clause applies to every line.' },
      ],
    },
    'back-office-data-processing': {
      title: 'Back-office and data processing',
      metaDescription: 'Back-office and data processing from the EU — order processing, KYC and onboarding, document handling, data entry and verification with four-eyes controls. ADD Individual Solutions.',
      heroDescription: 'Structured, measurable back-office work run under the same governance as the customer-facing lines: order processing, KYC and onboarding, document handling, data entry and verification — with four-eyes controls where accuracy is contractual.',
      sections: [
        { heading: 'Scope', content: 'Order and returns processing; KYC, onboarding and verification queues; document classification and data extraction; data entry and enrichment; case preparation for regulated processes. Delivered as standalone back-office capacity or bundled with front-office lines.' },
        { heading: 'How we deliver', content: 'Work is measured per unit and per hour against agreed accuracy and turnaround targets. Queues are managed with workforce-management discipline; peaks are absorbed by the shared team. Four-eyes review is applied to defined transaction types, and every unit of work is traceable.' },
        { heading: 'Quality and compliance', content: 'Accuracy, turnaround time and rework rate are reported weekly. EU-only processing under a GDPR data-processing agreement; role-based access; audit logs on every queue. Regulated processes (KYC, payments) follow your written procedures and sign-off rules.' },
        { heading: 'Commercial terms', content: 'Per FTE per month or per productive hour; unit pricing available for stable, well-defined queues. Back-office capacity is priced below the German-language lines and is most economical when bundled. Pilot-first entry with an exit clause.' },
      ],
    },
    'cx-consulting-ai': {
      title: 'CX consulting and AI-enabled CX',
      metaDescription: 'CX consulting and AI-enabled customer operations — operational assessment, QA and WFM design, CSAT/NPS programmes, agent assist, intelligent deflection and automated quality scoring. ADD Individual Solutions.',
      heroDescription: 'Operational consulting from an operator, and AI tooling built in-house and measured on KPIs. The assessment establishes the baseline; QA, WFM and CSAT/NPS design set the operating model; agent assist, deflection and automated scoring are introduced once the operation is stable.',
      sections: [
        { heading: 'Scope', content: 'Two-week CX Operations Assessment (volumes, channels, KPIs, cost-to-serve, QA baseline); design of QA frameworks, workforce-management processes and CSAT/NPS programmes; transition planning for in-house or outsourced operations. AI-enabled CX: agent assist, intelligent deflection, 100%-coverage quality scoring and contact-driver analytics.' },
        { heading: 'How we deliver', content: 'Consulting is delivered by the operator who would run the programme, not by a separate practice. AI tooling is developed by ADD’s own engineering team and deployed inside your existing desktop and knowledge base, with a human hand-off designed in from the start. Every tool carries a KPI it is accountable for.' },
        { heading: 'Quality and compliance', content: 'AI components are measured on AHT, FCR and QA score, not on demonstrations. Models run on EU infrastructure; customer data stays under your data-processing agreement. Calibration and final quality judgement remain with people.' },
        { heading: 'Commercial terms', content: 'The assessment is free for qualified operations of 20 FTE or more and ends with a pilot plan and price. Consulting engagements are scoped per project. AI-enabled CX is priced as part of the operation it supports, after the pilot has stabilised.' },
      ],
    },
  },
};

export type CxServiceSlug = keyof typeof cxEn.servicePages;
